import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { RiMicAiFill } from "react-icons/ri";
import { useI18n } from '@/i18n';
import { useProductStore } from '@/store/productStore';
import { ProductCard } from '@/components/ProductCard';
import { ProductCardSkeleton } from '@/components/skeletons/ProductCardSkeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Search, SlidersHorizontal } from 'lucide-react';

export function Products() {
  const { t, currentLang } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const { 
    products, 
    categories, 
    fetchProducts, 
    hasMore, 
    isLoading, 
    currentPage,
    initRealtimeSubscription,
    cleanupRealtimeSubscription
  } = useProductStore();

  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [priceRange, setPriceRange] = useState<[number, number]>([
    Number(searchParams.get('minPrice')) || 0,
    Number(searchParams.get('maxPrice')) || 5000
  ]);
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'newest');
  const isFeaturedOnly = searchParams.get('featured') === 'true';
  const [viewMode] = useState<'grid'>('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [isListening, setIsListening] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const recognitionRef = useRef<any>(null);
  const timeoutRef = useRef<number | null>(null);

  const startVoiceSearch = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert(currentLang === 'bn' ? 'আপনার ব্রাউজার ভয়েস সার্চ সাপোর্ট করে না' : 'Your browser does not support voice search');
      return;
    }

    if (isListening) {
      recognitionRef.current?.stop();
      if (timeoutRef.current !== null) clearTimeout(timeoutRef.current);
      setIsListening(false);
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = currentLang === 'bn' ? 'bn-BD' : 'en-US';
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognitionRef.current = recognition;

    recognition.onstart = () => {
      setIsListening(true);
      // Fallback: stop after 5s if no speech detected
      timeoutRef.current = window.setTimeout(() => {
        recognition.stop();
      }, 8000);
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    recognition.onresult = (event: any) => {
      if (timeoutRef.current !== null) clearTimeout(timeoutRef.current);
      const transcript = event.results[event.results.length - 1][0].transcript;
      setSearchQuery(transcript);
      recognition.stop();
    };
    recognition.onerror = () => { if (timeoutRef.current !== null) clearTimeout(timeoutRef.current); setIsListening(false); };
    recognition.onend = () => { if (timeoutRef.current !== null) clearTimeout(timeoutRef.current); setIsListening(false); };

    recognition.start();
  }, [isListening, currentLang]);

  useEffect(() => {
    const fetchCurrent = () => {
      fetchProducts({
        search: searchQuery,
        category: selectedCategory,
        limit: 20,
        page: 1,
        sort: sortBy,
        isFeatured: isFeaturedOnly ? true : undefined,
      });
    };

    fetchCurrent();
    
    // [SUPREME REACTIVITY SUITE]
    // 1. Real-time Subscription: Instant Price/Stock updates
    initRealtimeSubscription();

    // 2. Hibernation Sync: Re-fetch when user unlocks phone or returns to tab
    const handleSync = () => {
      if (document.visibilityState === 'visible') {
        if (import.meta.env.DEV) console.log('[Products] Focus detected. Re-syncing inventory...');
        fetchCurrent();
      }
    };

    window.addEventListener('visibilitychange', handleSync);
    window.addEventListener('focus', handleSync);
    window.addEventListener('online', handleSync);

    return () => {
      cleanupRealtimeSubscription();
      window.removeEventListener('visibilitychange', handleSync);
      window.removeEventListener('focus', handleSync);
      window.removeEventListener('online', handleSync);
    };
  }, [fetchProducts, searchQuery, selectedCategory, sortBy, isFeaturedOnly, initRealtimeSubscription, cleanupRealtimeSubscription]);

  // Filter products by price only (since price is complicated server-side without a custom RPC or complex .gte/.lte)
  const filteredProducts = useMemo(() => {
    return products.filter(
      (p) => p.price >= priceRange[0] && p.price <= priceRange[1]
    );
  }, [products, priceRange]);

  const loadMore = () => {
    fetchProducts({
      search: searchQuery,
      category: selectedCategory,
      limit: 20,
      page: currentPage + 1,
      sort: sortBy,
      isFeatured: isFeaturedOnly ? true : undefined,
      append: true,
    });
  };

  const handleSearch = (e?: React.FormEvent | React.MouseEvent) => {
    if (e) e.preventDefault();
    const params = new URLSearchParams(searchParams);
    if (searchQuery) {
      params.set('search', searchQuery);
    } else {
      params.delete('search');
    }
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
    setPriceRange([0, 500]);
    setSearchParams(new URLSearchParams());
  };

  const activeFiltersCount = [
    searchQuery,
    selectedCategory && selectedCategory !== 'all',
    priceRange[0] > 0 || priceRange[1] < 500,
  ].filter(Boolean).length;

  return (
    <div className="bg-white font-sans">
      {/* Premium Header — Gold Standard */}
      <header className="sticky top-0 z-50 w-full bg-white/80 backdrop-blur-xl border-b border-gray-100/50 shadow-[0_4px_20px_rgba(0,0,0,0.03)] transition-all">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-center">
          <h1 className="text-lg font-black tracking-tight text-center">{t('nav.products')}</h1>
        </div>
      </header>

      <div className="animate-fade-in pb-24 lg:pb-10">
        <div className="container mx-auto px-4">
        {/*<p className="text-gray-700 text-sm text-center mt-4 mb-4">
          {filteredProducts.length} {currentLang === 'bn' ? 'টি পণ্য পাওয়া গেছে' : 'products found'}
        </p>*/}
        {/* Search and Filters Bar */}
        <div className="bg-white rounded-xl p-0 mt-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search and Mobile Filter */}
            <div className="flex flex-1 gap-2">
              <form onSubmit={handleSearch} className="flex-1">
                <div className="relative">
                  <Search onClick={handleSearch} className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-600 cursor-pointer hover:text-primary transition-colors" />
                  <Input
                    type="search"
                    placeholder={t('search.placeholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-10 !border-none !outline-none !ring-0 focus-visible:!ring-0 focus-visible:!border-none shadow-[1px_2px_4px_rgba(0,0,0,.1)] bg-white text-black placeholder:text-gray-600 rounded-xl"
                  />
                  <button
                    type="button"
                    onClick={startVoiceSearch}
                    className={`absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full transition-all ${isListening
                        ? 'text-primary'
                        : 'text-primary bg-white lg:bg-transparent lg:hover:bg-white'
                      }`}
                  >
                    {isListening && (
                      <>
                        <style>{`
                          @keyframes spark {
                            0%, 100% { opacity: 0; transform: scale(0); }
                            50% { opacity: 1; transform: scale(1); }
                          }
                        `}</style>
                        {[0, 45, 90, 135, 180, 225, 270, 315].map((deg, i) => (
                          <span
                            key={deg}
                            className="absolute w-1 h-1 rounded-full bg-primary"
                            style={{
                              top: `calc(50% - 2px + ${Math.sin(deg * Math.PI / 180) * 14}px)`,
                              left: `calc(50% - 2px + ${Math.cos(deg * Math.PI / 180) * 14}px)`,
                              transform: 'translate(-50%, -50%)',
                              animation: `spark 1.2s ease-in-out ${i * 0.15}s infinite`,
                            }}
                          />
                        ))}
                      </>
                    )}
                    <RiMicAiFill className="h-4 w-4 relative z-10" />
                  </button>
                </div>
              </form>

              {/* Mobile Filter Button */}
              <Drawer open={showFilters} onOpenChange={setShowFilters} shouldScaleBackground={false}>
                <DrawerTrigger asChild>
                  <Button variant="outline" className="lg:hidden w-10 h-9 p-0 flex items-center justify-center shrink-0 border-none outline-none ring-0 focus-visible:ring-0 focus-visible:border-none shadow-[1px_2px_4px_rgba(0,0,0,.1)] bg-white rounded-xl hover:bg-transparent">
                    <SlidersHorizontal className="h-4 w-4 text-primary" />
                    {activeFiltersCount > 0 && (
                      <Badge variant="secondary" className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center rounded-full text-[10px] scale-75 bg-transparent text-primary">
                        {activeFiltersCount}
                      </Badge>
                    )}
                  </Button>
                </DrawerTrigger>
                <DrawerContent className="px-4 pb-8">
                  <DrawerHeader className="px-0 text-left">
                    <DrawerTitle>{t('btn.filter')}</DrawerTitle>
                  </DrawerHeader>
                  <div className="grid grid-cols-2 gap-2">
                    {/* Category Filter */}
                    <div>
                      <label className="text-sm font-medium mb-1 block text-gray-700">
                        {t('filter.category')}
                      </label>
                      <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                        <SelectTrigger className="w-full overflow-hidden align-end">
                          <SelectValue placeholder={t('filter.category')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t('btn.clear')}</SelectItem>
                          {categories.map((cat) => (
                            <SelectItem key={cat} value={cat}>
                              {t(`categories.${cat}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Sort */}
                    <div>
                      <label className="text-sm font-medium mb-1 block text-gray-700">
                        {t('filter.sort')}
                      </label>
                      <Select value={sortBy} onValueChange={setSortBy}>
                        <SelectTrigger className="w-full overflow-hidden">
                          <SelectValue placeholder={t('filter.sort')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="newest">{t('filter.sort.newest')}</SelectItem>
                          <SelectItem value="price_low">{t('filter.sort.price_low')}</SelectItem>
                          <SelectItem value="price_high">{t('filter.sort.price_high')}</SelectItem>
                          <SelectItem value="name">{t('filter.sort.name')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Price Range - full width row */}
                  <div className="mt-4">
                    <label className="text-sm text-center font-medium mb-1 block text-gray-700">
                      {t('filter.price')}: ৳{priceRange[0]} - ৳{priceRange[1]}
                    </label>
                    <div className="pt-2 px-2">
                      <Slider
                        value={priceRange}
                        onValueChange={(value) => setPriceRange(value as [number, number])}
                        max={500}
                        step={10}
                      />
                    </div>
                  </div>

                  <div className="mt-6 pt-4 border-none flex justify-end">
                    <Button onClick={() => { clearFilters(); setShowFilters(false); }} className="w-full sm:w-32 bg-white text-red-500 hover:bg-transparent">
                      {t('btn.clear')}
                    </Button>
                  </div>
                </DrawerContent>
              </Drawer>
            </div>

            {/* Desktop Filters */}
            <div className="hidden lg:flex items-center gap-4">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="gap-2 border-none shadow-[1px_2px_4px_rgba(0,0,0,.2),-1px_2px_4px_rgba(0,0,0,.2)] rounded-sm">
                    <SlidersHorizontal className="h-4 w-4" />
                    {t('btn.filter')}
                    {activeFiltersCount > 0 && (
                      <Badge variant="secondary" className="ml-1">
                        {activeFiltersCount}
                      </Badge>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[600px] p-6 shadow-xl" align="center">
                  <div className="grid grid-cols-2 gap-6">
                    {/* Category Filter */}
                    <div>
                      <label className="text-sm font-medium mb-1 block text-gray-700">
                        {t('filter.category')}
                      </label>
                      <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={t('filter.category')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t('btn.clear')}</SelectItem>
                          {categories.map((cat) => (
                            <SelectItem key={cat} value={cat}>
                              {t(`categories.${cat}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Sort */}
                    <div>
                      <label className="text-sm font-medium mb-1 block text-gray-700">
                        {t('filter.sort')}
                      </label>
                      <Select value={sortBy} onValueChange={setSortBy}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={t('filter.sort')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="newest">{t('filter.sort.newest')}</SelectItem>
                          <SelectItem value="price_low">{t('filter.sort.price_low')}</SelectItem>
                          <SelectItem value="price_high">{t('filter.sort.price_high')}</SelectItem>
                          <SelectItem value="name">{t('filter.sort.name')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Price Range - full width row */}
                  <div className="mt-6">
                    <label className="text-sm font-medium mb-3 block text-gray-700">
                      {t('filter.price')}: ৳{priceRange[0]} - ৳{priceRange[1]}
                    </label>
                    <div className="pt-2 px-2">
                      <Slider
                        value={priceRange}
                        onValueChange={(value) => setPriceRange(value as [number, number])}
                        max={500}
                        step={10}
                      />
                    </div>
                  </div>

                  <div className="mt-8 pt-4 border-none flex justify-end">
                    <Button onClick={clearFilters} variant="outline" className="w-32">
                      {t('btn.clear')}
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Active Filters 
          {activeFiltersCount > 0 && (
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-none">
              {searchQuery && (
                <Badge variant="secondary" className="gap-1">
                  {t('product.searched')}: {searchQuery}
                  <button type="button" onClick={() => setSearchQuery('')} className="ml-1">
                    <X className="h-3 w-3 cursor-pointer" />
                  </button>
                </Badge>
              )}
              {selectedCategory && selectedCategory !== 'all' && (
                <Badge variant="secondary" className="gap-1">
                  {t('filter.category')}: {t(`categories.${selectedCategory}`)}
                  <button type="button" onClick={() => setSelectedCategory('all')} className="ml-1">
                    <X className="h-3 w-3 cursor-pointer" />
                  </button>
                </Badge>
              )}
              {(priceRange[0] > 0 || priceRange[1] < 500) && (
                <Badge variant="secondary" className="gap-1">
                  ৳{priceRange[0]} - ৳{priceRange[1]}
                  <button type="button" onClick={() => setPriceRange([0, 500])} className="ml-1">
                    <X className="h-3 w-3 cursor-pointer" />
                  </button>
                </Badge>
              )}
            </div>
          )}
            */}
        </div>

        {/* Products Grid */}
        {isLoading && products.length === 0 ? (
          <motion.div
            key="skeletons"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={`grid ${viewMode === 'grid'
              ? 'grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-6'
              : 'grid-cols-1 gap-6'
            }`}
          >
            {Array.from({ length: 10 }).map((_, i) => (
              <ProductCardSkeleton key={i} />
            ))}
          </motion.div>
        ) : filteredProducts.length > 0 ? (
          <>
            <div
              className={`grid ${viewMode === 'grid'
                ? 'grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-6'
                : 'grid-cols-1 gap-6'
                }`}
            >
              <AnimatePresence mode="popLayout">
                {filteredProducts.map((product, index) => (
                  <motion.div
                    key={product.id}
                    layout
                    initial={{ opacity: 0, scale: 0.9, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ 
                      type: "spring", 
                      stiffness: 350, 
                      damping: 25, 
                      mass: 0.8,
                      opacity: { duration: 0.2 },
                      delay: index < 15 ? index * 0.04 : 0 
                    }}
                  >
                    <ProductCard product={product}/>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {hasMore && (
              <div className="mt-8 flex justify-center">
                <Button 
                  onClick={loadMore} 
                  variant="outline" 
                  className="rounded-full px-8 py-6 text-base font-bold"
                  disabled={isLoading}
                >
                  {isLoading ? 'Loading...' : (currentLang === 'bn' ? 'আরও দেখুন' : 'Load More')}
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-16">
            <div className="w-20 h-20 mx-auto mb-4 bg-gray-100 rounded-half flex items-center justify-center">
              <Search className="h-10 w-10 text-gray-600" />
            </div>
            <h3 className="text-xl font-semibold mb-2">{t('search.no_results')}</h3>
            <p className="text-black mb-4">
              {currentLang === 'bn'
                ? 'অন্যান্য ফিল্টার ব্যবহার করুন'
                : 'Try using different filters'}
            </p>
            <Button onClick={clearFilters}>{t('btn.clear')}</Button>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}